import React from "react";
import { FlaskConical, Loader2, RefreshCw, Lightbulb } from "lucide-react";
import { api, fmtBytes } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import NoProject from "@/components/NoProject";
import { PageHeader } from "@/components/finishing/Controls";

const Kpi = ({ label, value, sub, id }) => (
  <div className="studio-card p-4">
    <div className="metric-label">{label}</div>
    <div className="font-head text-2xl font-bold" data-testid={`beta-${id}`}>{value ?? "—"}</div>
    {sub && <div className="metric-label" style={{ fontSize: 9 }}>{sub}</div>}
  </div>
);

const Section = ({ title, children }) => (
  <div className="space-y-3"><h2 className="font-head text-lg font-bold">{title}</h2>{children}</div>
);

export default function BetaReport() {
  const { projectId } = useApp();
  const { data: r, refresh } = usePoll(() => (projectId ? api.betaReport(projectId) : Promise.resolve(null)), 15000, [projectId]);
  if (!projectId) return <NoProject title="Beta Report needs a project" />;
  if (!r) return <div className="p-8"><Loader2 className="animate-spin text-[#E6B04E]" /></div>;
  const { ingest: i, throughput: t, shot_selection: s, render: rn, mix, color: c } = r;

  return (
    <div className="p-8 fade-up space-y-8">
      <PageHeader icon={FlaskConical} title="Real-Footage Beta Report" subtitle="Measured from this project's actual media, jobs, edit plan and render — refreshes every 15s">
        <button className="btn btn-ghost" onClick={refresh} data-testid="btn-beta-refresh"><RefreshCw size={15} />Refresh</button>
      </PageHeader>

      <div className="studio-card p-5 border-[#E6B04E]/40" data-testid="beta-recommendations">
        <div className="flex items-center gap-2 mb-2"><Lightbulb size={16} className="text-[#E6B04E]" /><span className="font-semibold text-sm">Tuning recommendations</span></div>
        <ul className="space-y-1.5 text-sm text-[#CBD5E1] list-disc pl-5">{r.recommendations.map((x, k) => <li key={k}>{x}</li>)}</ul>
      </div>

      <Section title="Ingest">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Kpi id="files" label="Files" value={i.files} sub={`${i.videos} video · ${i.audio} audio`} />
          <Kpi id="bytes" label="Footage" value={fmtBytes(i.bytes)} sub={`${i.hours_of_footage} h`} />
          <Kpi id="scanned" label="Scanned" value={`${i.scanned}/${i.files}`} />
          <Kpi id="proxied" label="Proxies" value={`${i.proxied}/${i.videos}`} />
          <Kpi id="duplicates" label="Duplicates" value={i.duplicates} />
          <Kpi id="failed" label="Failed jobs" value={i.failed_jobs} />
        </div>
      </Section>

      <Section title={`Throughput · ${t.clips_per_hour ?? "—"} clips/hour · ${t.wall_hours} h wall time`}>
        <div className="studio-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="beta-throughput-table">
            <thead><tr className="metric-label text-left">{["Job", "Done", "Failed", "Queued", "Avg s", "P95 s", "Total min"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
            <tbody>{t.jobs.map((j) => (
              <tr key={j.type} className="border-t border-[#252A3B] font-mono text-xs">
                <td className="p-3">{j.type}</td><td className="p-3">{j.completed}</td><td className="p-3">{j.failed}</td><td className="p-3">{j.queued}</td>
                <td className="p-3">{j.avg_seconds ?? "—"}</td><td className="p-3">{j.p95_seconds ?? "—"}</td><td className="p-3">{j.total_minutes}</td>
              </tr>))}</tbody>
          </table>
        </div>
      </Section>

      <Section title="Shot selection">
        {!s ? <div className="studio-card p-5 text-sm text-[#64748B]">No edit plan yet — generate story + edit plan to measure shot selection.</div> : (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Kpi id="scenes" label="Scenes" value={s.scenes} sub={`${s.empty_scenes} empty`} />
            <Kpi id="shots" label="Shots" value={s.shots} sub={`${s.unique_clips_used} unique · ${s.reused_shots} reused`} />
            <Kpi id="coverage" label="Usable coverage" value={`${s.usable_coverage_pct}%`} sub={`of ${s.usable_clips} KEEP/GOOD`} />
            <Kpi id="fill" label="Avg scene fill" value={`${s.avg_fill_pct}%`} />
            <Kpi id="score" label="Selected score" value={s.avg_selected_score} sub={`pool avg ${s.avg_pool_score ?? "—"}`} />
            <Kpi id="events" label="Events covered" value={`${s.events_covered.length}/${s.events_tagged.length}`} sub={s.events_missing.length ? `missing: ${s.events_missing.slice(0, 3).join(", ")}` : "all covered"} />
          </div>
        )}
      </Section>

      <Section title="Crossfades & ducking (last render)">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Kpi id="segments" label="Rendered scenes" value={rn.segments} sub={rn.encoders.join(", ")} />
          <Kpi id="xfade-req" label="Crossfades requested" value={rn.crossfade_requested} />
          <Kpi id="xfade-ok" label="Applied" value={rn.crossfade_applied} />
          <Kpi id="xfade-fallback" label="Fell back to cut" value={rn.crossfade_fallbacks} />
          <Kpi id="ducked" label="Ducked scenes" value={rn.ducked_scenes} sub={`${mix.scenes_with_music_and_dialogue} need ducking`} />
          <Kpi id="mix" label="Ducking" value={mix.ducking ? "ON" : "OFF"} sub={`music ${Math.round(mix.music_volume * 100)}% · EQ ${mix.eq}`} />
        </div>
      </Section>

      <Section title="Color matching">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi id="cameras" label="Cameras" value={c.cameras} />
          <Kpi id="cam-spread" label="Camera spread" value={`${c.camera_spread_raw} → ${c.camera_spread_corrected}`} sub="lower = more consistent" />
          <Kpi id="scene-spread" label="Avg in-scene spread" value={c.avg_scene_spread_before != null ? `${c.avg_scene_spread_before} → ${c.avg_scene_spread_after}` : "—"} sub="with scene matching" />
          <Kpi id="scene-match" label="Scene matching" value={c.scene_match_enabled ? "ON" : "OFF"} />
        </div>
      </Section>
    </div>
  );
}
